// components/VideoPlayer/SubtitleOverlay.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import {
  useSubtitleSettings,
  DISPLAY_MODES,
} from "../../context/SubtitleSettingsContext";
import { useShadowTechnique } from "../../context/ShadowTechniqueContext";
import "./SubtitleOverlay.css";
import ClickableSubtitle from "./ClickableSubtitle/ClickableSubtitle";
import { TranslateButton, SaveButton } from "./SubtitleButtons/SubtitleButtons";

const SubtitleOverlay = () => {
  const {
    showSubtitles,
    subtitle1,
    subtitle2,
    setSubtitle1,
    setSubtitle2,
    setIsDraggingSub,
    isDraggingSub,
    setIsHoveringSub1,
    setIsHoveringSub2,
    pauseVideo,
    playVideo,
    isHoveringSub1,
    isHoveringSub2,
    videoSrc,
    videoRef,
    subtitle1Loaded,
    subtitle2Loaded,
    videoMetadata,
    isFullscreen: isVideoFullscreen,
    toggleFullscreen: toggleVideoFullscreen,
    isModalOpen,
  } = useVideo();

  const {
    settings,
    undercoverActive,
    setUndercoverHover,
    DISPLAY_MODES,
    getEffectiveDisplayMode,
    toggleSubtitle1Display, // 👈 اضافه شد
    toggleSubtitle2Display, // 👈 اضافه شد
  } = useSubtitleSettings();

  const { isActive: isShadowActive } = useShadowTechnique();

  const [wasVideoFullscreenBeforePopup, setWasVideoFullscreenBeforePopup] =
    useState(false);

  const [displaySub1, setDisplaySub1] = useState("");
  const [displaySub2, setDisplaySub2] = useState("");

  const playStateRef = useRef(false);
  const activeSubtitleRef = useRef(null);
  const sub1BoxRef = useRef(null);
  const sub2BoxRef = useRef(null);

  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // ===============================
  useEffect(() => {
    const handleUserInteraction = () => {
      if (wasVideoFullscreenBeforePopup && !isVideoFullscreen) {
        toggleVideoFullscreen();
        setWasVideoFullscreenBeforePopup(false);
      }
    };

    window.addEventListener("click", handleUserInteraction);

    return () => {
      window.removeEventListener("click", handleUserInteraction);
    };
  }, [wasVideoFullscreenBeforePopup, isVideoFullscreen, toggleVideoFullscreen]);

  // ===============================
  useEffect(() => {
    const handleSub1Change = (e) => {
      setDisplaySub1(e.detail);
      setUndercoverHover(1, false);
    };

    const handleSub2Change = (e) => {
      setDisplaySub2(e.detail);
      setUndercoverHover(2, false);
    };

    window.addEventListener("subtitle1change", handleSub1Change);
    window.addEventListener("subtitle2change", handleSub2Change);

    return () => {
      window.removeEventListener("subtitle1change", handleSub1Change);
      window.removeEventListener("subtitle2change", handleSub2Change);
    };
  }, [setUndercoverHover]);

  // ===============================
  const getContainerRect = () => {
    const el = document.querySelector(".video-wrapper");
    return el?.getBoundingClientRect();
  };

  const toPx = (pos) => {
    const rect = getContainerRect();
    if (!rect) return { x: 0, y: 0 };

    return {
      x: pos.x * rect.width,
      y: pos.y * rect.height,
    };
  };

  const toPercent = (x, y) => {
    const rect = getContainerRect();
    if (!rect) return { x: 0, y: 0 };

    return {
      x: x / rect.width,
      y: y / rect.height,
    };
  };

  // ===============================
  const setDefaultPositions = useCallback(() => {
    const rect = getContainerRect();
    if (!rect) return;

    const centerX = 0.5;
    const englishY = 0.75;
    const persianY = 0.85;

    setSubtitle1({ x: centerX, y: englishY });
    setSubtitle2({ x: centerX, y: persianY });
  }, [setSubtitle1, setSubtitle2]);

  useEffect(() => {
    if (videoSrc) {
      setTimeout(setDefaultPositions, 100);
    }
  }, [videoSrc, setDefaultPositions]);

  // ===============================
  const handleDragStart = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (isModalOpen) {
      return;
    }

    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    const boxRef = id === 1 ? sub1BoxRef.current : sub2BoxRef.current;
    if (!boxRef) return;

    const rect = getContainerRect();
    if (!rect) return;

    const subPos = id === 1 ? subtitle1 : subtitle2;
    const subPx = toPx(subPos);

    const offsetX = clientX - rect.left - subPx.x;
    const offsetY = clientY - rect.top - subPx.y;

    dragOffsetRef.current = { x: offsetX, y: offsetY };

    activeSubtitleRef.current = id;
    setIsDraggingSub(id);

    if (videoRef.current) {
      playStateRef.current = !videoRef.current.paused;
      pauseVideo();
    }

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDrag, { passive: false });
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDrag = (e) => {
    e.preventDefault();

    const id = activeSubtitleRef.current;
    if (!id) return;

    const rect = getContainerRect();
    if (!rect) return;

    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    const newX = clientX - rect.left - dragOffsetRef.current.x;
    const newY = clientY - rect.top - dragOffsetRef.current.y;

    const percent = toPercent(newX, newY);

    const clamped = {
      x: Math.max(0, Math.min(1, percent.x)),
      y: Math.max(0, Math.min(1, percent.y)),
    };

    if (id === 1) setSubtitle1(clamped);
    else setSubtitle2(clamped);
  };

  const handleDragEnd = () => {
    activeSubtitleRef.current = null;
    setIsDraggingSub(null);

    if (playStateRef.current) {
      playVideo();
      playStateRef.current = false;
    }

    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDrag);
    document.removeEventListener("touchend", handleDragEnd);
  };

  // ===============================
  const handleMouseEnter = (id) => {
    if (!isDraggingSub) {
      playStateRef.current = !videoRef.current.paused;
      pauseVideo();
    }

    id === 1 ? setIsHoveringSub1(true) : setIsHoveringSub2(true);
    setUndercoverHover(id, true);
  };

  const handleMouseLeave = (id) => {
    id === 1 ? setIsHoveringSub1(false) : setIsHoveringSub2(false);
    setUndercoverHover(id, false);

    if (!isDraggingSub && playStateRef.current) {
      playVideo();
      playStateRef.current = false;
    }
  };

  // ===============================
  // 👈 تابع کلیک روی دکمه چشم
  const handleEyeClick = (e, subNumber) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation(); // 👈 مهم: جلوگیری از رسیدن رویداد به ویدیو

    if (subNumber === 1) {
      toggleSubtitle1Display?.();
    } else {
      toggleSubtitle2Display?.();
    }
  };

  // ===============================
  const shouldShowSubtitle = (subNumber) => {
    const effectiveMode = getEffectiveDisplayMode(subNumber);
    if (effectiveMode === DISPLAY_MODES.HIDDEN) return false;
    if (isShadowActive) return true;
    return showSubtitles;
  };

  if (!showSubtitles && !isShadowActive) return null;

  const sub1Px = toPx(subtitle1);
  const sub2Px = toPx(subtitle2);

  // 👈 آیا زیرنویس لود شده و تنظیمات چشم فعاله
  const showEyeButton1 = subtitle1Loaded && settings.subtitle1.showEyeButton;
  const showEyeButton2 = subtitle2Loaded && settings.subtitle2.showEyeButton;

  // 👈 آیا زیرنویس در حالت نمایش هست
  const isSub1Visible = getEffectiveDisplayMode(1) !== DISPLAY_MODES.HIDDEN;
  const isSub2Visible = getEffectiveDisplayMode(2) !== DISPLAY_MODES.HIDDEN;

  // 👈 آیا الان متنی برای زیرنویس وجود داره
  const hasSub1Text = !!displaySub1;
  const hasSub2Text = !!displaySub2;

  // 👈 آیا باید دکمه چشم تنها (پنهان) نشون داده بشه - فقط وقتی متن داریم
  const showEyeOnly1 = showEyeButton1 && !isSub1Visible && hasSub1Text;
  const showEyeOnly2 = showEyeButton2 && !isSub2Visible && hasSub2Text;

  // 👈 آیا زیرنویس باکس باید نشون داده بشه
  const showSub1Box =
    isSub1Visible && hasSub1Text && subtitle1Loaded && shouldShowSubtitle(1);
  const showSub2Box =
    isSub2Visible && hasSub2Text && subtitle2Loaded && shouldShowSubtitle(2);

  return (
    <div className="subtitle-overlay" onClick={(e) => e.stopPropagation()}>
      {/* 👈 دکمه چشم زیرنویس ۱ - فقط وقتی زیرنویس پنهانه ولی متن داریم */}
      {showEyeOnly1 && (
        <div
          className="subtitle-eye-only"
          style={{
            left: sub1Px.x,
            top: sub1Px.y,
            fontSize: `${settings.subtitle1.fontSize}px`,
          }}
          onClick={(e) => handleEyeClick(e, 1)}
          onMouseDown={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
          onTouchStart={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
          title="نمایش زیرنویس ۱"
        >
          <i className="fas fa-eye-slash"></i>
        </div>
      )}

      {/* زیرنویس اول */}
      {showSub1Box && (
        <div
          ref={sub1BoxRef}
          className={`subtitle-box subtitle-english ${
            isHoveringSub1 ? "hovering" : ""
          } ${
            getEffectiveDisplayMode(1) === DISPLAY_MODES.UNDERCOVER &&
            !undercoverActive[1]
              ? "undercover-mode"
              : ""
          }`}
          style={{
            left: sub1Px.x,
            top: sub1Px.y,
            transform: "translate(-50%, -50%)",
            fontSize: `${settings.subtitle1.fontSize}px`,
            color: settings.subtitle1.color,
            backgroundColor: settings.subtitle1.bgColor,
            fontFamily:
              settings.subtitle1.language === "fa" ? "Vazirmatn" : "Roboto",
            direction: settings.subtitle1.language === "fa" ? "rtl" : "ltr",
            pointerEvents: isModalOpen ? "none" : "auto",
            opacity: isModalOpen ? 0.5 : 1,
            transition: "opacity 0.2s ease",
          }}
          onMouseDown={(e) => handleDragStart(e, 1)}
          onTouchStart={(e) => handleDragStart(e, 1)}
          onMouseEnter={() => handleMouseEnter(1)}
          onMouseLeave={() => handleMouseLeave(1)}
        >
          <div
            className="subtitle-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 👈 دکمه چشم در کنار متن زیرنویس */}
            {showEyeButton1 && (
              <button
                className="subtitle-eye-btn"
                onClick={(e) => handleEyeClick(e, 1)}
                onMouseDown={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
                title="پنهان کردن زیرنویس ۱"
              >
                <i className="fas fa-eye"></i>
              </button>
            )}

            {settings.subtitle1.showTranslateButton && (
              <TranslateButton
                subtitleNumber={1}
                fromLang={settings.subtitle1.language}
                toLang={settings.subtitle2.language}
                text={displaySub1}
                service={settings.subtitle1.translateService}
                onOpen={() => {
                  if (isVideoFullscreen) {
                    setWasVideoFullscreenBeforePopup(true);
                  }
                }}
              />
            )}

            <ClickableSubtitle
              text={displaySub1}
              onWordClick={() => {}}
              subtitleNumber={1}
            />

            {settings.subtitle1.showSaveButton && (
              <SaveButton
                text={displaySub1}
                fromLang={settings.subtitle1.language}
                toLang={settings.subtitle2.language}
                subtitle1Text={displaySub1}
                subtitle2Text={displaySub2}
                movieName={videoMetadata?.filmName || ""}
                season={videoMetadata?.season || ""}
                episode={videoMetadata?.episode || ""}
                onOpen={() => {
                  if (isVideoFullscreen) {
                    setWasVideoFullscreenBeforePopup(true);
                  }
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* 👈 دکمه چشم زیرنویس ۲ - فقط وقتی زیرنویس پنهانه ولی متن داریم */}
      {showEyeOnly2 && (
        <div
          className="subtitle-eye-only"
          style={{
            left: sub2Px.x,
            top: sub2Px.y,
            fontSize: `${settings.subtitle2.fontSize}px`,
          }}
          onClick={(e) => handleEyeClick(e, 2)}
          onMouseDown={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
          onTouchStart={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
          title="نمایش زیرنویس ۲"
        >
          <i className="fas fa-eye-slash"></i>
        </div>
      )}

      {/* زیرنویس دوم */}
      {showSub2Box && (
        <div
          ref={sub2BoxRef}
          className={`subtitle-box subtitle-persian ${
            isHoveringSub2 ? "hovering" : ""
          } ${
            getEffectiveDisplayMode(2) === DISPLAY_MODES.UNDERCOVER &&
            !undercoverActive[2]
              ? "undercover-mode"
              : ""
          }`}
          style={{
            left: sub2Px.x,
            top: sub2Px.y,
            transform: "translate(-50%, -50%)",
            fontSize: `${settings.subtitle2.fontSize}px`,
            color: settings.subtitle2.color,
            backgroundColor: settings.subtitle2.bgColor,
            fontFamily: "Vazirmatn, Tahoma, sans-serif",
            direction: "rtl",
            pointerEvents: isModalOpen ? "none" : "auto",
            opacity: isModalOpen ? 0.5 : 1,
            transition: "opacity 0.2s ease",
          }}
          onMouseDown={(e) => handleDragStart(e, 2)}
          onTouchStart={(e) => handleDragStart(e, 2)}
          onMouseEnter={() => handleMouseEnter(2)}
          onMouseLeave={() => handleMouseLeave(2)}
        >
          <div
            className="subtitle-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 👈 دکمه چشم در کنار متن زیرنویس */}
            {showEyeButton2 && (
              <button
                className="subtitle-eye-btn"
                onClick={(e) => handleEyeClick(e, 2)}
                onMouseDown={(e) => e.stopPropagation()} // 👈 جلوگیری از drag
                title="پنهان کردن زیرنویس ۲"
              >
                <i className="fas fa-eye"></i>
              </button>
            )}

            {settings.subtitle2.showTranslateButton && (
              <TranslateButton
                subtitleNumber={2}
                fromLang={settings.subtitle1.language}
                toLang={settings.subtitle2.language}
                text={displaySub2}
                service={settings.subtitle2.translateService}
                onOpen={() => {
                  if (isVideoFullscreen) {
                    setWasVideoFullscreenBeforePopup(true);
                  }
                }}
              />
            )}

            <ClickableSubtitle
              text={displaySub2}
              onWordClick={() => {}}
              subtitleNumber={2}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtitleOverlay;
